import { Checkbox, FormControlLabel, FormGroup, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import { MonthlyMode, PeriodicMode, QuarterlyMode, Repeat, YearlyMode } from "../../../api/Tasks";
import { Dropdown } from "../../../misc/Dropdown";
import { NthDayOfWeekPicker } from "./NthDayOfWeekPicker";
import { monthNames, seedRepeat, weekdayNames, weekdayShortNames } from "../repeatText";
import { panelFontSize } from "./PanelSection";

const monthOptions = monthNames.map((name, i) => ({ value: i + 1, label: name }));

const radioSx = { "& .MuiFormControlLabel-label": { fontSize: panelFontSize }, marginLeft: "-6px" };

// trim the checkbox down so all seven weekdays fit the panel width
const weekdaySx = { padding: "2px", "& .MuiSvgIcon-root": { fontSize: 16 } };

// match the due-date field at the top of the panel (see DueDatePanel)
const numberFieldSx = {
    "& .MuiInputBase-root": { fontSize: panelFontSize },
    "& .MuiInputBase-input": { padding: "4px 6px" },
};

// RepeatDetails is the per-kind part of the repeat config; the kind itself is
// picked in the row above it (see DueDatePanel).
export function RepeatDetails(props: {
    value: Repeat;
    dueDate: string | null;
    onChange: (value: Repeat) => void;
}) {
    const repeat = props.value;

    function update(changes: Partial<Repeat>) {
        props.onChange({ ...repeat, ...changes });
    }

    function seed(changes: {
        monthlyMode?: MonthlyMode;
        quarterlyMode?: QuarterlyMode;
        yearlyMode?: YearlyMode;
        periodicMode?: PeriodicMode;
    }) {
        props.onChange(seedRepeat(repeat, changes, props.dueDate));
    }

    if (repeat.kind === "weekly") {
        return (
            <FormGroup row style={{ justifyContent: "space-between", flexWrap: "nowrap" }}>
                {weekdayShortNames.map((name, day) => (
                    <FormControlLabel
                        key={day}
                        label={name}
                        labelPlacement="bottom"
                        style={{ margin: 0 }}
                        slotProps={{ typography: { variant: "caption" } }}
                        control={
                            <Checkbox
                                size="small"
                                sx={weekdaySx}
                                checked={repeat.weekdays.includes(day)}
                                slotProps={{ input: { "aria-label": weekdayNames[day] } }}
                                onChange={e => update({
                                    weekdays: e.target.checked
                                        ? [...repeat.weekdays, day].sort((a, b) => a - b)
                                        : repeat.weekdays.filter(d => d !== day),
                                })}
                            />
                        }
                    />
                ))}
            </FormGroup>
        );
    }

    // monthly and quarterly pick the day the same way; they differ only in which month
    // it's picked out of (see core/task/repeat.go)
    if (repeat.kind === "monthly" || repeat.kind === "quarterly") {
        const quarterly = repeat.kind === "quarterly";
        const mode = quarterly ? repeat.quarterlyMode : repeat.monthlyMode;

        return (
            <RadioGroup value={mode}
                        onChange={e => seed(quarterly
                            ? { quarterlyMode: e.target.value as QuarterlyMode }
                            : { monthlyMode: e.target.value as MonthlyMode })}>
                <FormControlLabel value="day-of-month" sx={radioSx} control={<Radio size="small" />} label={
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        On day
                        <TextField type="number" size="small" style={{ width: 60 }} sx={numberFieldSx}
                                   disabled={mode !== "day-of-month"}
                                   value={repeat.dayOfMonth}
                                   onChange={e => update({ dayOfMonth: parseInt(e.target.value) || 0 })} />
                    </span>
                } />
                <FormControlLabel value="nth-day-of-week" sx={radioSx} control={<Radio size="small" />} label={
                    <NthDayOfWeekPicker repeat={repeat} disabled={mode !== "nth-day-of-week"}
                                        onChange={update} />
                } />
            </RadioGroup>
        );
    }

    if (repeat.kind === "yearly") {
        return (
            <RadioGroup value={repeat.yearlyMode}
                        onChange={e => seed({ yearlyMode: e.target.value as YearlyMode })}>
                <FormControlLabel value="same-date" sx={radioSx} control={<Radio size="small" />}
                                  label="Same date next year" />
                <FormControlLabel value="nth-day-of-week" sx={radioSx} control={<Radio size="small" />} label={
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <NthDayOfWeekPicker repeat={repeat} disabled={repeat.yearlyMode !== "nth-day-of-week"}
                                            onChange={update} />
                        of
                        <Dropdown width={110} disabled={repeat.yearlyMode !== "nth-day-of-week"}
                                  value={repeat.month} options={monthOptions}
                                  onChange={value => update({ month: value })} />
                    </span>
                } />
            </RadioGroup>
        );
    }

    if (repeat.kind === "periodically") {
        return (
            <>
                <Typography variant="caption" color="text.secondary">
                    Counted from the completed task's due date, or the day it was completed when it had none.
                </Typography>
                <RadioGroup value={repeat.periodicMode}
                            onChange={e => seed({ periodicMode: e.target.value as PeriodicMode })}>
                    <FormControlLabel value="days" sx={radioSx} control={<Radio size="small" />} label={
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <TextField type="number" size="small" style={{ width: 60 }} sx={numberFieldSx}
                                       disabled={repeat.periodicMode !== "days"}
                                       value={repeat.days}
                                       onChange={e => update({ days: parseInt(e.target.value) || 0 })} />
                            days later
                        </span>
                    } />
                    <FormControlLabel value="nth-day-of-week" sx={radioSx} control={<Radio size="small" />} label={
                        <NthDayOfWeekPicker repeat={repeat} disabled={repeat.periodicMode !== "nth-day-of-week"}
                                            onChange={update} allowLast={false} />
                    } />
                </RadioGroup>
            </>
        );
    }

    return null;
}
